import React, { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import './EventAnalytics.css';

interface TicketAnalytics {
  totalTickets: number;
  soldTickets: number;
  remainingTickets: number;
  revenue: number;
  categories: CategoryAnalytics[];
  dailySales: DailySales[];
  recentSales: TicketSale[];
}

interface CategoryAnalytics {
  categoryId: string;
  categoryName: string;
  totalTickets: number;
  soldTickets: number;
  revenue: number;
  percentage: number;
}

interface DailySales {
  date: string;
  sales: number;
  revenue: number;
}

interface TicketSale {
  id: string;
  userEmail: string;
  userName: string;
  categoryName: string;
  price: number;
  purchaseDate: string;
  status: string;
}

interface EventAnalyticsProps {
  eventId: string;
  onClose: () => void;
}

type TimeFilter = 'week' | 'month' | 'quarter' | 'year';
type ChartType = 'sales' | 'revenue' | 'categories';

const EventAnalytics: React.FC<EventAnalyticsProps> = ({ eventId, onClose }) => {
  const [analytics, setAnalytics] = useState<TicketAnalytics | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [timeFilter, setTimeFilter] = useState<TimeFilter>('month');
  const [chartType, setChartType] = useState<ChartType>('sales');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchName, setSearchName] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [eventTitle, setEventTitle] = useState('Event');

  const fetchEventTitle = useCallback(async () => {
    try {
      const events = await apiService.getEvents();
      const event = events.find(e => e.id === eventId);
      if (event) {
        setEventTitle(event.title);
      }
    } catch (err) {
      console.error('Error fetching event title:', err);
    }
  }, [eventId]);

  const fetchAnalytics = useCallback(async () => {
    try {
      setLoading(true);
      
      // Try to fetch from API first
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/analytics/event/${eventId}?timeFilter=${timeFilter}`, {
          headers: {
            'X-API-Key': process.env.REACT_APP_API_KEY || '',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          setAnalytics(data);
          return;
        }
      } catch (apiError) {
        console.log('API not available, using mock data');
      }

      // Fallback to mock data for demonstration
      const mockData: TicketAnalytics = {
        totalTickets: 500,
        soldTickets: 325,
        remainingTickets: 175,
        revenue: 8125.50,
        categories: [
          {
            categoryId: '1',
            categoryName: 'General Admission',
            totalTickets: 300,
            soldTickets: 200,
            revenue: 4000,
            percentage: 61.5
          },
          {
            categoryId: '2',
            categoryName: 'VIP',
            totalTickets: 100,
            soldTickets: 75,
            revenue: 3750,
            percentage: 23.1
          },
          {
            categoryId: '3',
            categoryName: 'Student',
            totalTickets: 100,
            soldTickets: 50,
            revenue: 375.50,
            percentage: 15.4
          }
        ],
        dailySales: [
          { date: '2025-01-28', sales: 45, revenue: 1125 },
          { date: '2025-01-29', sales: 52, revenue: 1300 },
          { date: '2025-01-30', sales: 38, revenue: 950 },
          { date: '2025-01-31', sales: 67, revenue: 1675 },
          { date: '2025-02-01', sales: 43, revenue: 1075 },
          { date: '2025-02-02', sales: 80, revenue: 2000 },
        ],
        recentSales: [
          {
            id: '1',
            userEmail: 'john.doe@example.com',
            userName: 'John Doe',
            categoryName: 'VIP',
            price: 50,
            purchaseDate: '2025-02-02T14:30:00Z',
            status: 'confirmed'
          },
          {
            id: '2',
            userEmail: 'jane.smith@example.com',
            userName: 'Jane Smith',
            categoryName: 'General Admission',
            price: 20,
            purchaseDate: '2025-02-02T13:15:00Z',
            status: 'confirmed'
          },
          {
            id: '3',
            userEmail: 'student@university.edu',
            userName: 'Alex Johnson',
            categoryName: 'Student',
            price: 7.5,
            purchaseDate: '2025-02-02T11:45:00Z',
            status: 'confirmed'
          },
          {
            id: '4',
            userEmail: 'mary.wilson@gmail.com',
            userName: 'Mary Wilson',
            categoryName: 'VIP',
            price: 50,
            purchaseDate: '2025-02-01T16:20:00Z',
            status: 'confirmed'
          },
          {
            id: '5',
            userEmail: 'bob.brown@yahoo.com',
            userName: 'Bob Brown',
            categoryName: 'General Admission',
            price: 20,
            purchaseDate: '2025-02-01T12:10:00Z',
            status: 'confirmed'
          }
        ]
      };
      
      setAnalytics(mockData);
    } catch (err) {
      console.error('Error fetching analytics:', err);
      setError('Failed to load analytics data');
    } finally {
      setLoading(false);
    }
  }, [eventId, timeFilter]);

  useEffect(() => {
    fetchAnalytics();
    fetchEventTitle();
  }, [fetchAnalytics, fetchEventTitle]);

  const exportToExcel = async () => {
    try {
      setExporting(true);
      
      // Try to export from API first
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/export/event/${eventId}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_API_KEY || '',
          },
          body: JSON.stringify({
            format: 'excel',
            includeAnalytics: true,
            timeFilter,
            filters: {
              email: searchEmail,
              name: searchName,
              category: selectedCategory,
              dateFrom,
              dateTo
            }
          }),
        });

        if (response.ok) {
          const blob = await response.blob();
          const url = window.URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.style.display = 'none';
          a.href = url;
          a.download = `event-${eventTitle}-analytics.xlsx`;
          document.body.appendChild(a);
          a.click();
          window.URL.revokeObjectURL(url);
          return;
        }
      } catch (apiError) {
        console.log('API not available for export');
      }

      // Fallback: Create a simple CSV download
      if (analytics) {
        const csvData = [
          ['Customer Name', 'Email', 'Category', 'Price', 'Purchase Date', 'Status'],
          ...analytics.recentSales.map(ticket => [
            ticket.userName,
            ticket.userEmail,
            ticket.categoryName,
            `€${ticket.price.toFixed(2)}`,
            new Date(ticket.purchaseDate).toLocaleDateString(),
            ticket.status
          ])
        ];
        
        const csvContent = csvData.map(row => row.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = `event-${eventTitle}-analytics.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
      setError('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const renderChart = () => {
    if (!analytics) return null;

    switch (chartType) {
      case 'sales':
        return renderSalesChart();
      case 'revenue':
        return renderRevenueChart();
      case 'categories':
        return renderCategoriesChart();
      default:
        return null;
    }
  };

  const renderSalesChart = () => {
    if (!analytics?.dailySales.length) return <div className="no-data">No sales data available</div>;

    const maxSales = Math.max(...analytics.dailySales.map(d => d.sales));
    
    return (
      <div className="chart-container">
        <h4>Daily Ticket Sales</h4>
        <div className="bar-chart">
          {analytics.dailySales.map((day, index) => (
            <div key={index} className="bar-item">
              <div 
                className="bar"
                style={{ height: `${(day.sales / maxSales) * 100}%` }}
                title={`${day.date}: ${day.sales} tickets`}
              />
              <span className="bar-label">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderRevenueChart = () => {
    if (!analytics?.dailySales.length) return <div className="no-data">No revenue data available</div>;

    const maxRevenue = Math.max(...analytics.dailySales.map(d => d.revenue));
    
    return (
      <div className="chart-container">
        <h4>Daily Revenue</h4>
        <div className="bar-chart">
          {analytics.dailySales.map((day, index) => (
            <div key={index} className="bar-item">
              <div 
                className="bar revenue-bar"
                style={{ height: `${(day.revenue / maxRevenue) * 100}%` }}
                title={`${day.date}: €${day.revenue.toFixed(2)}`}
              />
              <span className="bar-label">{new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCategoriesChart = () => {
    if (!analytics?.categories.length) return <div className="no-data">No category data available</div>;

    return (
      <div className="chart-container">
        <h4>Sales by Category</h4>
        <div className="pie-chart">
          {analytics.categories.map((category, index) => (
            <div key={index} className="category-item">
              <div className="category-bar">
                <div 
                  className="category-fill"
                  style={{ width: `${category.percentage}%` }}
                />
              </div>
              <div className="category-info">
                <span className="category-name">{category.categoryName}</span>
                <span className="category-stats">{category.soldTickets}/{category.totalTickets} (€{category.revenue.toFixed(2)})</span>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const filteredTickets = analytics?.recentSales.filter(ticket => {
    const matchesEmail = !searchEmail || ticket.userEmail.toLowerCase().includes(searchEmail.toLowerCase());
    const matchesName = !searchName || ticket.userName.toLowerCase().includes(searchName.toLowerCase());
    const matchesCategory = !selectedCategory || ticket.categoryName === selectedCategory;
    const matchesDateFrom = !dateFrom || new Date(ticket.purchaseDate) >= new Date(dateFrom);
    const matchesDateTo = !dateTo || new Date(ticket.purchaseDate) <= new Date(dateTo);
    
    return matchesEmail && matchesName && matchesCategory && matchesDateFrom && matchesDateTo;
  }) || [];

  const paginatedTickets = filteredTickets.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredTickets.length / itemsPerPage);

  if (loading) {
    return (
      <div className="analytics-modal">
        <div className="analytics-content">
          <div className="loading">Loading analytics...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="analytics-modal">
        <div className="analytics-content">
          <div className="error">{error}</div>
          <button onClick={onClose} className="btn btn-secondary">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="analytics-modal">
      <div className="analytics-content">
        <div className="analytics-header">
          <h2>Analytics for {eventTitle}</h2>
          <button onClick={onClose} className="close-btn">&times;</button>
        </div>

        {analytics && (
          <>
            {/* Summary Cards */}
            <div className="analytics-summary">
              <div className="summary-card">
                <h3>Total Tickets</h3>
                <div className="metric">{analytics.totalTickets}</div>
              </div>
              <div className="summary-card">
                <h3>Sold Tickets</h3>
                <div className="metric">{analytics.soldTickets}</div>
                <div className="sub-metric">
                  {analytics.totalTickets > 0 ? ((analytics.soldTickets / analytics.totalTickets) * 100).toFixed(1) : 0}% sold
                </div>
              </div>
              <div className="summary-card">
                <h3>Remaining</h3>
                <div className="metric">{analytics.remainingTickets}</div>
              </div>
              <div className="summary-card">
                <h3>Revenue</h3>
                <div className="metric">€{analytics.revenue.toFixed(2)}</div>
              </div>
            </div>

            {/* Chart Controls */}
            <div className="chart-controls">
              <div className="filter-group">
                <label>Time Period:</label>
                <select 
                  value={timeFilter} 
                  onChange={(e) => setTimeFilter(e.target.value as TimeFilter)}
                  aria-label="Time filter"
                >
                  <option value="week">Last Week</option>
                  <option value="month">Last Month</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
              </div>
              
              <div className="filter-group">
                <label>Chart Type:</label>
                <select 
                  value={chartType} 
                  onChange={(e) => setChartType(e.target.value as ChartType)}
                  aria-label="Chart type"
                >
                  <option value="sales">Daily Sales</option>
                  <option value="revenue">Daily Revenue</option>
                  <option value="categories">By Category</option>
                </select>
              </div>

              <button 
                onClick={exportToExcel} 
                disabled={exporting}
                className="btn btn-primary export-btn"
              >
                {exporting ? 'Exporting...' : '📊 Export to Excel'}
              </button>
            </div>

            {/* Chart Display */}
            <div className="chart-section">
              {renderChart()}
            </div>

            {/* Ticket List */}
            <div className="ticket-list-section">
              <h3>Ticket Sales</h3>
              
              {/* Filters */}
              <div className="ticket-filters">
                <div className="filter-row">
                  <input
                    type="text"
                    placeholder="Search by email..."
                    value={searchEmail}
                    onChange={(e) => setSearchEmail(e.target.value)}
                    className="filter-input"
                  />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchName}
                    onChange={(e) => setSearchName(e.target.value)}
                    className="filter-input"
                  />
                  <select 
                    value={selectedCategory} 
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    className="filter-select"
                    aria-label="Category filter"
                  >
                    <option value="">All Categories</option>
                    {analytics.categories.map(cat => (
                      <option key={cat.categoryId} value={cat.categoryName}>
                        {cat.categoryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="filter-row">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="filter-input"
                    placeholder="From date"
                  />
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="filter-input"
                    placeholder="To date"
                  />
                  <button 
                    onClick={() => {
                      setSearchEmail('');
                      setSearchName('');
                      setSelectedCategory('');
                      setDateFrom('');
                      setDateTo('');
                      setCurrentPage(1);
                    }}
                    className="btn btn-secondary"
                  >
                    Clear Filters
                  </button>
                </div>
              </div>

              {/* Ticket Table */}
              <div className="ticket-table">
                <table>
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Email</th>
                      <th>Category</th>
                      <th>Price</th>
                      <th>Purchase Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedTickets.map(ticket => (
                      <tr key={ticket.id}>
                        <td>{ticket.userName}</td>
                        <td>{ticket.userEmail}</td>
                        <td>{ticket.categoryName}</td>
                        <td>€{ticket.price.toFixed(2)}</td>
                        <td>{new Date(ticket.purchaseDate).toLocaleDateString()}</td>
                        <td>
                          <span className={`status ${ticket.status.toLowerCase()}`}>
                            {ticket.status}
                          </span>
                        </td>
                        <td>
                          <button 
                            className="btn btn-sm btn-outline"
                            onClick={() => window.open(`/ticket/${ticket.id}`, '_blank')}
                          >
                            View Details
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="pagination">
                  <button 
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(prev => prev - 1)}
                    className="btn btn-sm"
                  >
                    Previous
                  </button>
                  
                  <span className="page-info">
                    Page {currentPage} of {totalPages} 
                    ({filteredTickets.length} tickets)
                  </span>
                  
                  <button 
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(prev => prev + 1)}
                    className="btn btn-sm"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EventAnalytics;
